import * as yup from 'yup';
import { PhotoCategory } from '../types';

// Photo upload form validation schema
export const photoUploadSchema = yup.object().shape({
  project_id: yup.string().required('Project selection is required'),

  files: yup
    .array()
    .of(yup.mixed<File>().required())
    .min(1, 'At least one photo is required')
    .max(10, 'Maximum 10 photos allowed')
    .test('fileSize', 'Each file must be less than 10MB', files => {
      if (!files) return true;
      return files.every(file => {
        if (file instanceof File) {
          return file.size <= 10 * 1024 * 1024; // 10MB
        }
        return true;
      });
    })
    .test('fileType', 'Only image files are allowed', files => {
      if (!files) return true;
      return files.every(file => {
        if (file instanceof File) {
          return file.type.startsWith('image/');
        }
        return true;
      });
    })
    .required('Photos are required'),

  title: yup
    .string()
    .trim()
    .min(3, 'Title must be at least 3 characters')
    .max(100, 'Title must not exceed 100 characters')
    .required('Title is required'),

  category: yup
    .mixed<PhotoCategory>()
    .oneOf(
      [
        'progress',
        'safety',
        'inspection',
        'material_delivery',
        'issue',
        'quality_control',
        'before_after',
        'documentation',
        'completion',
        'other',
      ],
      'Please select a valid category'
    )
    .required('Category is required'),

  number_on_image: yup
    .string()
    .trim()
    .max(50, 'Tagging ID must not exceed 50 characters')
    .optional()
    .default(''),

  location: yup
    .object()
    .shape({
      site: yup
        .string()
        .trim()
        .max(100, 'Site name must not exceed 100 characters')
        .required('Site is required'),

      building: yup
        .string()
        .trim()
        .max(100, 'Building name must not exceed 100 characters')
        .optional()
        .default(''),

      floor: yup
        .string()
        .trim()
        .max(50, 'Floor must not exceed 50 characters')
        .optional()
        .default(''),

      room: yup
        .string()
        .trim()
        .max(50, 'Room must not exceed 50 characters')
        .optional()
        .default(''),

      gps: yup.string().trim().optional().default(''),
    })
    .required(),

  description: yup
    .string()
    .trim()
    .max(500, 'Description must not exceed 500 characters')
    .optional()
    .default(''),

  tags: yup.array().of(yup.string().required()).optional().default([]),
});

// Photo filter form validation schema
export const photoFilterSchema = yup.object().shape({
  search: yup.string().trim().max(100, 'Search term must not exceed 100 characters').optional(),

  category: yup
    .mixed<PhotoCategory | ''>()
    .oneOf(
      [
        'progress',
        'safety',
        'inspection',
        'material_delivery',
        'issue',
        'quality_control',
        'before_after',
        'documentation',
        'completion',
        'other',
        '',
      ],
      'Please select a valid category'
    )
    .optional(),

  project_id: yup.string().trim().optional(),

  location: yup.string().trim().max(100, 'Location must not exceed 100 characters').optional(),

  uploaded_by: yup
    .string()
    .trim()
    .max(100, 'Uploaded by must not exceed 100 characters')
    .optional(),

  sort_by: yup
    .string()
    .oneOf(
      ['uploaded_at', 'title', 'category', 'tagging_id', 'size'],
      'Please select a valid sort field'
    )
    .optional(),

  sort_order: yup.string().oneOf(['asc', 'desc'], 'Please select a valid sort order').optional(),

  date_from: yup
    .string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
    .test('valid-date', 'Please enter a valid date', value => {
      if (!value) return true;
      const date = new Date(value);
      return !isNaN(date.getTime());
    })
    .optional(),

  date_to: yup
    .string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
    .test('valid-date', 'Please enter a valid date', value => {
      if (!value) return true;
      const date = new Date(value);
      return !isNaN(date.getTime());
    })
    .test('date-range', 'End date must be after start date', function (value) {
      if (!value || !this.parent.date_from) return true;
      return new Date(value) >= new Date(this.parent.date_from);
    })
    .optional(),
});

// Type definitions for form data
export type PhotoUploadFormData = yup.InferType<typeof photoUploadSchema>;
export type PhotoFilterFormData = yup.InferType<typeof photoFilterSchema>;
